import { SPACE_COLUMNS, SPACE_FIELDS, type SpaceField, type SpaceKind } from '@sivan/shared';
import { type SyntheticEvent, useState } from 'react';
import { Modal } from './Modal';
import { type SpaceRow, spaceDraft as draft } from './space-draft';

interface SpaceFormProps {
  readonly row: SpaceRow;
  readonly kind: SpaceKind;
  readonly allowed: (row: SpaceRow | null, key: string) => boolean;
  readonly onApply: (key: string, changes: readonly { field: SpaceField; value: string }[]) => void;
  readonly onClose: () => void;
}

/**
 * Mobile editing of one space into the same draft as the grid. Emptying a stored value, or ticking
 * "סיום הצמדה", is an explicit clear that the review shows before anything is saved.
 */
export function SpaceForm({ row, kind, allowed, onApply, onClose }: SpaceFormProps) {
  const initial = Object.fromEntries(
    SPACE_FIELDS.map((f) => [f, draft.displayValue(row, f)]),
  ) as Record<SpaceField, string>;
  const [values, setValues] = useState(initial);
  const [end, setEnd] = useState(false);
  const editable = (field: SpaceField) =>
    draft.lockedReason(row, field) === null && allowed(row.unitId === null ? null : row, field);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const changes = SPACE_FIELDS.filter((f) => editable(f) && values[f] !== initial[f]).map(
      (field) => ({
        field,
        value: values[field],
      }),
    );
    if (end) changes.push({ field: 'apartment', value: '' });
    onApply(row.key, changes);
  };
  const one = kind === 'parking' ? 'חניה' : 'מחסן';
  return (
    <Modal
      title={row.unitId === null ? `${one} חדש/ה` : `עריכת ${one} ${initial.number}`}
      open
      onClose={onClose}
    >
      <form className="unit-form" onSubmit={submit}>
        {SPACE_COLUMNS[kind].map((column) => {
          const id = `space-${column.key}`;
          return (
            <div key={column.key} className="field">
              <label htmlFor={id}>
                {column.header}
                {column.secondary === null ? '' : ` (${column.secondary})`}
              </label>
              <input
                id={id}
                value={values[column.key]}
                disabled={
                  !editable(column.key) ||
                  (end && (column.key === 'building' || column.key === 'apartment'))
                }
                onChange={(event) => {
                  setValues({ ...values, [column.key]: event.target.value });
                }}
              />
            </div>
          );
        })}
        {row.unitId !== null && initial.apartment !== '' && editable('apartment') && (
          <label className="toolbar__toggle">
            <input
              type="checkbox"
              checked={end}
              onChange={(event) => {
                setEnd(event.target.checked);
              }}
            />
            <span>סיום ההצמדה לדירה</span>
          </label>
        )}
        <footer className="modal__footer">
          <button type="submit" className="button button--primary">
            עדכון הטיוטה
          </button>
          <button type="button" className="button" onClick={onClose}>
            ביטול
          </button>
        </footer>
      </form>
    </Modal>
  );
}
